import React, { ChangeEvent, SyntheticEvent, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';

import ArrowsDown from '@app/illu/Misc/ArrowsDown.svg';

import Crowd from '@app/illu/Large/Crowd.svg';
import Briefcase from '@app/illu/Large/Briefcase.svg';
import Both from '@app/illu/Large/Both.svg';

import Physical from '@app/illu/Large/Physical.svg';
import Remote from '@app/illu/Large/VideoCall.svg';
import Unmoderated from '@app/illu/Large/Survey.svg';

import {
  IllustratedSelectionBlock,
  TextAndImageSelectionBlock,
} from '@app/form';
import {
  AnimatedSlidingDiv,
  Button,
  InformationBox,
  useToast,
} from '@app/core';
import {
  StudySubjectPreference,
  StudyTypePreference,
  useUpdateParticipantField,
} from '@app/api';
import { useNavigate } from 'react-router-dom';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  welcomeBox: {
    padding: [[theme.marginBase * 2, theme.marginBase]],

    position: 'relative',
    overflow: 'hidden',
    minHeight: theme.marginBase * 13,
    width: '100%',

    backgroundColor: theme.colors.greenMuted,
    borderRadius: theme.borderRadius.std,

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  welcomeBoxTitle: {
    ...theme.fonts.h4,
  },
  welcomeBoxText: {
    ...theme.fonts.body,
  },
  arrowsDownLeft: {
    position: 'absolute',
    top: 0,
    left: 0,
    [theme.media.mobile]: {
      display: 'none',
    },
  },
  arrowsDownRight: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    [theme.media.mobile]: {
      display: 'none',
    },
  },

  section: {
    marginTop: theme.marginBase * 7,
  },
  buttonSection: {
    marginTop: theme.marginBase * 7,
    textAlign: 'center',
  },

  label: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 3,
  },
  formatLine: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  laterButton: {
    marginLeft: theme.marginBase * 2,
  },
  boxTextImageFirst: {
    marginBottom: theme.marginBase * 2,
    flex: 1,
    [theme.media.tablet]: {
      flex: 'none',
    },
  },
  boxTextImageElse: {
    marginBottom: theme.marginBase * 2,
    flex: 1,
    marginLeft: theme.marginBase * 2,
    [theme.media.tablet]: {
      flex: 'none',
      marginLeft: 0,
    },
  },
}));

interface Props {
  participantId: string;
  allowContinueLater?: boolean;
  onSuccess: (info: {
    studyTypePreferences: string[];
    studySubjectPreferences: string[];
  }) => any;
  className?: string;
}

export const StudyPreferences = (props: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const [studySubjectPreferences, setStudySubjectPreferences] = useState<
    StudySubjectPreference | '' | 'BOTH'
  >('');
  const [studyTypePreferences, setStudyTypePreferences] = useState<string[]>(
    [],
  );

  const navigate = useNavigate();

  const [submitted, setSubmited] = useState(false);
  const { mutate: updateField } = useUpdateParticipantField(
    props.participantId,
  );

  const onSubjectChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      const { value } = e.currentTarget;
      setStudySubjectPreferences(value as typeof studySubjectPreferences);
      updateField({
        fieldName: 'studySubjectPreferences',
        choices:
          value === 'BOTH'
            ? [StudySubjectPreference.B2C, StudySubjectPreference.B2B]
            : [value],
      });
    } else {
      setStudySubjectPreferences('');
      updateField({ fieldName: 'studySubjectPreferences', choices: [] });
    }
  };
  const onTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      setStudyTypePreferences([...studyTypePreferences, e.currentTarget.value]);
    } else {
      setStudyTypePreferences(
        studyTypePreferences.filter((v) => v !== e.currentTarget.value),
      );
    }
  };

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    updateField({
      fieldName: 'studyTypePreferences',
      choices: studyTypePreferences,
    });
    toast.saved();

    if (props.onSuccess) {
      await props.onSuccess({
        studyTypePreferences,
        studySubjectPreferences:
          studySubjectPreferences === 'BOTH'
            ? [StudySubjectPreference.B2C, StudySubjectPreference.B2B]
            : [studySubjectPreferences],
      });
    }
  };
  const onSubmitLater = (e: SyntheticEvent) => {
    e.preventDefault();
    updateField({
      fieldName: 'studyTypePreferences',
      choices: studyTypePreferences,
    });
    setSubmited(true);
    toast.saved();
    navigate('/profile');
  };

  return (
    <form className={props.className} onSubmit={onSubmit}>
      <AnimatedSlidingDiv isOpen={!submitted} maxHeight={2000}>
        <div className={classes.welcomeBox}>
          <img
            className={classes.arrowsDownLeft}
            alt="Arrow down"
            src={ArrowsDown}
          />
          <img
            className={classes.arrowsDownRight}
            alt="Arrow down"
            src={ArrowsDown}
          />
          <h2 className={classes.welcomeBoxTitle}>
            <Trans i18nKey="panelInformations.welcomeBox.title" />
          </h2>
          <p className={classes.welcomeBoxText}>
            <Trans i18nKey="panelInformations.welcomeBox.text" />
          </p>
        </div>

        <section className={classes.section}>
          <h2 className={classes.label}>
            <Trans i18nKey="panelInformations.studySubjectPref.title" />
          </h2>
          <div className={classes.formatLine}>
            <TextAndImageSelectionBlock
              illustration={Crowd}
              title={t('panelInformations.studySubjectPref.B2C.title')}
              value={StudySubjectPreference.B2C}
              description={t('panelInformations.studySubjectPref.B2C.text')}
              onChange={onSubjectChange}
              checked={studySubjectPreferences === StudySubjectPreference.B2C}
              className={classes.boxTextImageFirst}
            />
            <TextAndImageSelectionBlock
              illustration={Briefcase}
              title={t('panelInformations.studySubjectPref.B2B.title')}
              value={StudySubjectPreference.B2B}
              description={t('panelInformations.studySubjectPref.B2B.text')}
              onChange={onSubjectChange}
              checked={studySubjectPreferences === StudySubjectPreference.B2B}
              className={classes.boxTextImageElse}
            />
            <TextAndImageSelectionBlock
              illustration={Both}
              title={t('panelInformations.studySubjectPref.BOTH.title')}
              value="BOTH"
              description={t('panelInformations.studySubjectPref.BOTH.text')}
              checked={studySubjectPreferences === 'BOTH'}
              onChange={onSubjectChange}
              className={classes.boxTextImageElse}
            />
          </div>
        </section>
        <AnimatedSlidingDiv isOpen={!!studySubjectPreferences} maxHeight={1000}>
          <section className={classes.section}>
            <h2 className={classes.label}>
              <Trans i18nKey="panelInformations.studyTypePref.title" />
            </h2>
            <div className={classes.formatLine}>
              <IllustratedSelectionBlock
                illustration={Physical}
                title={t('panelInformations.studyTypePref.PHYSICAL.title')}
                value={StudyTypePreference.PHYSICAL}
                description={t('panelInformations.studyTypePref.PHYSICAL.text')}
                onChange={onTypeChange}
                checked={studyTypePreferences.includes(
                  StudyTypePreference.PHYSICAL,
                )}
                type="checkbox"
              />
              <IllustratedSelectionBlock
                illustration={Remote}
                title={t('panelInformations.studyTypePref.REMOTE.title')}
                value={StudyTypePreference.REMOTE}
                description={t('panelInformations.studyTypePref.REMOTE.text')}
                onChange={onTypeChange}
                checked={studyTypePreferences.includes(
                  StudyTypePreference.REMOTE,
                )}
                type="checkbox"
              />
              <IllustratedSelectionBlock
                illustration={Unmoderated}
                title={t('panelInformations.studyTypePref.UNMODERATED.title')}
                value={StudyTypePreference.UNMODERATED}
                description={t(
                  'panelInformations.studyTypePref.UNMODERATED.text',
                )}
                onChange={onTypeChange}
                checked={studyTypePreferences.includes(
                  StudyTypePreference.UNMODERATED,
                )}
                type="checkbox"
              />
            </div>
          </section>
          <section className={classes.section}>
            <InformationBox
              title={t('panelInformations.continueInformationsBlock.title')}
              text={t('panelInformations.continueInformationsBlock.text')}
            />
          </section>

          <section className={classes.buttonSection}>
            <Button type="submit" withNextArrow>
              <Trans i18nKey="panelInformations.confirmPref" />
            </Button>
            {props.allowContinueLater && (
              <Button
                type="button"
                variant="transparent"
                className={classes.laterButton}
                onClick={onSubmitLater}
              >
                <Trans i18nKey="panelInformations.continueLater" />
              </Button>
            )}
          </section>
        </AnimatedSlidingDiv>
      </AnimatedSlidingDiv>
    </form>
  );
};
